import type React from "react";
import { useMemo, useState } from "react";
import type { FieldArrayWithId, UseFieldArrayMove, UseFieldArrayRemove, UseFieldArrayUpdate } from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";

import { IconButton } from "@/components/Button/IconButton";
import {
  DynamicRepeater,
  type DynamicRepeaterDragEndEvent,
  type DynamicRepeaterDragStartEvent,
} from "@/components/DynamicRepeater/DynamicRepeater";
import { DynamicRepeaterItem } from "@/components/DynamicRepeater/DynamicRepeaterItem";
import { DynamicRepeaterItemOverlay } from "@/components/DynamicRepeater/DynamicRepeaterItemOverlay";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { FormQuestion } from "@/components/Survey/FormQuestion";
import { questionTypes } from "@/queries/surveys/constants";
import { useTranslation } from "@/translations";

import type { CreateMassSurveyFormValues } from "../CreateMassSurveyPage";

type MassSurveyFormQuestionsProps = {
  questionFields: FieldArrayWithId<CreateMassSurveyFormValues, "questions">[];
  removeQuestion: UseFieldArrayRemove;
  updateQuestion: UseFieldArrayUpdate<CreateMassSurveyFormValues, "questions">;
  moveQuestion: UseFieldArrayMove;
};

export function MassSurveyFormQuestions({
  questionFields,
  removeQuestion,
  updateQuestion,
  moveQuestion,
}: MassSurveyFormQuestionsProps): React.ReactNode {
  const form = useFormContext<CreateMassSurveyFormValues>();
  const { t } = useTranslation();
  const [dragItemId, setDragItemId] = useState<number | null>(null);

  const category = useWatch({ control: form.control, name: "category" });

  function handleDragStart(event: DynamicRepeaterDragStartEvent) {
    setDragItemId(event.active.data.current?.sortable.index);
  }

  function handleDragEnd(event: DynamicRepeaterDragEndEvent) {
    setDragItemId(null);
    const { active, over } = event;

    if (over && active.id !== over.id) {
      moveQuestion(active.data.current?.sortable.index, over?.data.current?.sortable.index);
    }
  }

  const isUseForAnalysisSelected = useMemo(() => {
    return questionFields.some((field) => field.useForAnalysis);
  }, [questionFields]);

  return (
    <DynamicRepeater
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      itemIds={questionFields.map((field) => field.id)}
    >
      {questionFields.map((question, idx) => (
        <DynamicRepeaterItem
          key={question.id}
          id={question.id}
          title={t("page.surveys.create-or-edit.question.label", { count: idx + 1 })}
          headerActions={
            questionFields.length > 1 ? (
              <IconButton
                size="sm"
                variant="filled"
                tone="error"
                title={t("page.surveys.create-or-edit.question.delete")}
                onClick={() => removeQuestion(idx)}
                icon={<Trash02Icon />}
              />
            ) : undefined
          }
        >
          <FormQuestion<CreateMassSurveyFormValues>
            idx={idx}
            control={form.control}
            updateField={updateQuestion}
            types={questionTypes}
            question={question}
            showOptions={question.type === "multipleChoice"}
            showUseForAnalysisOption={category === "feelAtHome"}
            isUseForAnalysisDisabled={isUseForAnalysisSelected && !question.useForAnalysis}
          />
        </DynamicRepeaterItem>
      ))}

      <DynamicRepeaterItemOverlay
        show={dragItemId !== null}
        title={t("page.surveys.create-or-edit.question.label", { count: dragItemId! + 1 })}
        headerActions={
          questionFields.length > 1 ? (
            <IconButton
              size="sm"
              variant="filled"
              tone="error"
              title={t("page.surveys.create-or-edit.question.delete")}
              onClick={() => null}
              icon={<Trash02Icon />}
              disabled
            />
          ) : undefined
        }
      >
        <FormQuestion<CreateMassSurveyFormValues>
          idx={dragItemId!}
          control={form.control}
          updateField={() => null}
          types={questionTypes}
          question={questionFields[dragItemId!]}
          showOptions={questionFields[dragItemId!]?.type === "multipleChoice"}
          showUseForAnalysisOption={category === "feelAtHome"}
          isUseForAnalysisDisabled={isUseForAnalysisSelected && !questionFields[dragItemId!]?.useForAnalysis}
        />
      </DynamicRepeaterItemOverlay>
    </DynamicRepeater>
  );
}
